import { useEffect, useMemo, useRef, useState } from "react";
import {
  restoreLocations,
  locationImage,
  distanceMiles,
  type RestoreLocation,
} from "@/lib/restore-locations";
import { PhoneLink } from "@/components/site/PhoneLink";
import { mainPhone } from "@/lib/site-core";
import { ServiceAreaCardBody } from "@/components/site/ServiceAreaCard";
import {
  MAIN_MARKETS_LABEL,
  VIRTUAL_CONSULT_PATH,
  serviceAreaCityLine,
  serviceAreaForSlug,
  servingLabel,
} from "@/lib/service-areas";

const MAPS_KEY =
  (import.meta.env["VITE_GOOGLE_MAPS_API_KEY"] as string | undefined) ||
  "AIzaSyDMIkwxmaO08msMlUPioGuRRVjVVXNH_gc";

const MARKER_ICON =
  "/img/r/wp-2023-01-restore-location-marker-1-alpha.webp";

const GRAYSCALE = [{ featureType: "all", elementType: "all", stylers: [{ saturation: -100 }] }];

declare global {
  interface Window {
    google?: any;
    __restoreMapsCallback?: () => void;
    gm_authFailure?: () => void;
  }
}

function loadMaps(): Promise<any> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.maps) return Promise.resolve(window.google);
  const existing = document.querySelector<HTMLScriptElement>("script[data-restore-maps]");
  return new Promise((resolve, reject) => {
    const done = () => (window.google?.maps ? resolve(window.google) : reject(new Error("maps failed")));
    if (existing) {
      existing.addEventListener("load", done);
      existing.addEventListener("error", () => reject(new Error("maps failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}`;
    s.async = true;
    s.defer = true;
    s.dataset["restoreMaps"] = "1";
    s.addEventListener("load", done);
    s.addEventListener("error", () => reject(new Error("maps failed")));
    document.head.appendChild(s);
  });
}

function esc(v: string) {
  return v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function infoHtml(loc: RestoreLocation) {
  const img = locationImage(loc.slug);
  const area = serviceAreaForSlug(loc.slug);
  if (area) {
    // Out-of-market office: same wording as its card (ServiceAreaCard.tsx).
    return `<div style="display:flex;gap:12px;max-width:360px;font-family:inherit">
    <div><p style="margin:0;font-weight:800;font-size:15px">${esc(servingLabel(area))}</p>
    <p style="margin:4px 0 0;font-size:13px;color:#444">${esc(area.street)}<br/>${esc(serviceAreaCityLine(area))}</p>
    <p style="margin:6px 0 0;font-size:12px;color:#444"><strong>Main Locations:</strong> ${esc(MAIN_MARKETS_LABEL)}</p>
    <div style="margin-top:10px"><a href="${VIRTUAL_CONSULT_PATH}" style="display:inline-block;background:#c8a45c;color:#111;font-weight:700;font-size:12px;letter-spacing:.04em;text-transform:uppercase;padding:8px 14px;text-decoration:none">Book a Virtual Consultation</a></div></div>
    ${img ? `<img loading="lazy" decoding="async" alt="${esc(servingLabel(area))}" src="${img}" style="width:110px;height:90px;object-fit:cover" />` : ""}
  </div>`;
  }
  const addr = [loc.address1, loc.address2].filter(Boolean).join(", ");
  const btn = loc.bookingUrl
    ? `<div style="margin-top:10px"><a href="${loc.bookingUrl}" target="_blank" rel="noopener" style="display:inline-block;background:#c8a45c;color:#111;font-weight:700;font-size:12px;letter-spacing:.04em;padding:8px 14px;text-decoration:none">${loc.bookingText}</a></div>`
    : "";
  return `<div style="display:flex;gap:12px;max-width:360px;font-family:inherit">
    <div><p style="margin:0;font-weight:800;font-size:15px">${loc.title}</p>
    <p style="margin:4px 0 0;font-size:13px;color:#444">${addr}</p>${btn}</div>
    ${img ? `<img loading="lazy" decoding="async" alt="${loc.title}" src="${img}" style="width:110px;height:90px;object-fit:cover" />` : ""}
  </div>`;
}

export function RestoreLocationsMap({
  title = "RESTORE® Hair Locations",
  hideList = false,
}: {
  title?: string;
  hideList?: boolean;
}) {
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const infoRef = useRef<any>(null);
  const [mapError, setMapError] = useState(false);

  const [query, setQuery] = useState("");
  const [radius, setRadius] = useState(50);
  const [results, setResults] = useState<Array<RestoreLocation & { miles?: number }> | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    window.gm_authFailure = () => {
      if (!cancelled) setMapError(true);
    };
    loadMaps()
      .then((google) => {
        if (cancelled || !mapEl.current) return;
        const map = new google.maps.Map(mapEl.current, {
          center: { lat: 41.850033, lng: -87.6500523 },
          zoom: 4,
          mapTypeControlOptions: { mapTypeIds: [google.maps.MapTypeId.ROADMAP, "restore-grayscale"] },
        });
        const styled = new google.maps.StyledMapType(GRAYSCALE, { name: "Grayscale" });
        map.mapTypes.set("restore-grayscale", styled);
        map.setMapTypeId("restore-grayscale");
        mapRef.current = map;
        infoRef.current = new google.maps.InfoWindow();
        restoreLocations.forEach((loc) => {
          const marker = new google.maps.Marker({
            position: { lat: loc.lat, lng: loc.lng },
            map,
            icon: MARKER_ICON,
            title: loc.title,
          });
          marker.addListener("click", () => {
            infoRef.current.setContent(infoHtml(loc));
            infoRef.current.open({ anchor: marker, map });
          });
        });
      })
      .catch(() => !cancelled && setMapError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const alphabetical = useMemo(
    () => [...restoreLocations].sort((a, b) => a.title.localeCompare(b.title)),
    [],
  );

  async function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) {
      setError("This field can not be empty.");
      return;
    }
    setError("");
    setSearching(true);
    try {
      const google = await loadMaps();
      const geocoder = new google.maps.Geocoder();
      const res = await geocoder.geocode({ address: `${q}, USA` });
      const point = res?.results?.[0]?.geometry?.location;
      if (!point) {
        setResults([]);
      } else {
        const origin = { lat: point.lat(), lng: point.lng() };
        const near = restoreLocations
          .map((l) => ({ ...l, miles: distanceMiles(origin, l) }))
          .filter((l) => l.miles <= radius)
          .sort((a, b) => a.miles - b.miles);
        setResults(near);
        if (mapRef.current) {
          mapRef.current.setCenter(origin);
          mapRef.current.setZoom(near.length ? 8 : 6);
        }
      }
    } catch {
      setError("We couldn't search right now. Please try again.");
    } finally {
      setSearching(false);
    }
  }

  function reset() {
    setQuery("");
    setRadius(50);
    setResults(null);
    setError("");
    if (mapRef.current) {
      mapRef.current.setCenter({ lat: 41.850033, lng: -87.6500523 });
      mapRef.current.setZoom(4);
    }
  }

  const list = results ?? alphabetical;

  return (
    <section className="bg-muted py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <h2 className="text-center text-3xl font-extrabold leading-tight text-foreground md:text-5xl">
          {title}
        </h2>

        <div className="mt-8 overflow-hidden border border-border bg-background shadow-sm">
          <div ref={mapEl} className="h-[420px] w-full md:h-[520px]" aria-label="Map of RESTORE Hair locations" />
          {mapError && (
            <p className="p-4 text-center text-sm text-muted-foreground">
              The interactive map couldn&rsquo;t load. Browse all locations below.
            </p>
          )}
        </div>

        <form
          onSubmit={onSearch}
          className="mt-8 grid gap-4 border border-border bg-background p-5 sm:grid-cols-[1fr_180px_auto_auto] sm:items-end"
        >
          <div>
            <label htmlFor="loc-search" className="block text-xs font-bold uppercase tracking-wider text-foreground">
              Zip code or city
            </label>
            <input
              id="loc-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter zip code or city"
              className="mt-2 w-full border border-input bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>
          <div>
            <label htmlFor="loc-radius" className="block text-xs font-bold uppercase tracking-wider text-foreground">
              Radius
            </label>
            <select
              id="loc-radius"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="mt-2 w-full border border-input bg-background px-3 py-2 text-sm text-foreground"
            >
              {[20, 50, 100, 250].map((r) => (
                <option key={r} value={r}>
                  {r} miles
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={searching}
            className="bg-primary px-6 py-2.5 text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
          >
            {searching ? "Please wait…" : "Search"}
          </button>
          <button
            type="button"
            onClick={reset}
            className="border border-foreground px-6 py-2.5 text-sm font-bold tracking-wider text-foreground transition hover:bg-foreground hover:text-background"
          >
            Reset
          </button>
          {error && <p className="text-sm text-destructive sm:col-span-4">{error}</p>}
        </form>

        {results && results.length === 0 && (
          <p className="mt-6 text-center text-sm text-foreground">
            No locations found within {radius} miles. Try a larger radius.
          </p>
        )}

        {!hideList && (
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {list.map((loc) => {
              const img = locationImage(loc.slug);
              return (
                <article
                  key={loc.slug}
                  className="grid grid-cols-1 overflow-hidden border border-border bg-background shadow-sm sm:grid-cols-[1fr_180px]"
                >
                  {serviceAreaForSlug(loc.slug) ? (
                    <div className="flex flex-col p-5">
                      <ServiceAreaCardBody area={serviceAreaForSlug(loc.slug)!} />
                      {"miles" in loc && typeof loc.miles === "number" && (
                        <p className="mt-3 text-sm font-semibold text-foreground">{loc.miles.toFixed(1)} miles away</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col p-5">
                      <h3 className="inline-block self-start border-b-2 border-primary pb-1 text-xl font-bold text-foreground">
                        {loc.title}
                      </h3>
                      <div className="mt-4 text-sm text-foreground/80">
                        <p>{[loc.address1, loc.address2].filter(Boolean).join(", ")}</p>
                        {loc.phoneLabel && (
                          <p>
                            {/* Same link as the other clinic cards; see PhoneLink.tsx. */}
                            <PhoneLink phone={mainPhone} />
                          </p>
                        )}
                        {"miles" in loc && typeof loc.miles === "number" && (
                          <p className="mt-1 font-semibold text-foreground">{loc.miles.toFixed(1)} miles away</p>
                        )}
                      </div>
                      {loc.mapLink && (
                        <a
                          href={loc.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-6 self-start text-sm font-bold text-foreground underline underline-offset-4 hover:no-underline"
                        >
                          View Map<span className="sr-only"> for {loc.title}</span>
                        </a>
                      )}
                      {loc.bookingUrl && (
                        <a
                          href={loc.bookingUrl}
                          target={loc.bookingUrl.startsWith("http") ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          className="mt-4 inline-block self-start bg-primary px-5 py-3 text-center text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90"
                        >
                          {loc.bookingText}
                        </a>
                      )}
                    </div>
                  )}
                  {img && (
                    <img
                      src={img}
                      alt={`${loc.title} RESTORE Hair location`}
                      loading="lazy"
                      className="h-48 w-full object-cover sm:h-full"
                    />
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
